import React, { useState, forwardRef } from "react";
import { IconContext } from "react-icons";
import { TbUsersGroup, TbBuildingBank, TbMessages } from "react-icons/tb";
import { RiCalendarEventFill } from "react-icons/ri";
import { LuPencilRuler } from "react-icons/lu";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import { useUserPreferences } from "../../../common/contexts/UserPreferencesContext";

interface Action {
  title: string;
  description: string;
}

interface ActionsSummaryProps {
  actions: Record<number, Action[]>;
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
  showPdfContent: boolean;
}

const ActionsSummary = forwardRef<HTMLDivElement, ActionsSummaryProps>(
  ({ actions, isExpanded, setIsExpanded, showPdfContent }, ref) => {
    const { translations } = useUserPreferences();
    const [selectedCategory, setSelectedCategory] = useState<number>(1);

    const getCategoryName = (categoryId: number) => {
      switch (categoryId) {
        case 1: return translations.tmsPeople || "People";
        case 2: return translations.tmsOrganization || "Organization";
        case 3: return translations.tmsPlan || "Plan";
        case 4: return translations.tmsTool || "Tools";
        case 5: return translations.tmsCommunication || "Communication";
        default: return "Unknown";
      }
    };

    const getCategoryColorFull = (categoryId: number) => {
      switch (categoryId) {
        case 1: return '#002395'; // People
        case 2: return '#d28ac8'; // Organization
        case 3: return '#003551'; // Plan
        case 4: return '#ffa100'; // Tools
        case 5: return '#0065bd'; // Communication
        default: return '#ccc';   // Unknown
      }
    };

    const icon = (categoryId: number) => {
      switch (categoryId) {
        case 1: return <TbUsersGroup />;
        case 2: return <TbBuildingBank />;
        case 3: return <RiCalendarEventFill />;
        case 4: return <LuPencilRuler />;
        case 5: return <TbMessages />;
        default: return null;
      }
    };

    const totalActions = Object.values(actions).reduce(
      (sum, categoryActions) => sum + categoryActions.length,
      0
    );

    const renderSelectedCategory = () => {
      const categoryActions = actions[selectedCategory] || [];
      if (categoryActions.length === 0) return <p>No actions available for this category.</p>;

      return (
        <div className="actions-summary__content">
          <div className="category-section">
            {categoryActions.map((action, index) => (
              <div key={index} className="action-item">
                <h4>{action.title}</h4>
                <p>{action.description}</p>
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderAllCategoriesForPDF = () => {
      console.log("Rendering PDF categories for ActionsSummary:", actions);
      return (
        <div
          ref={ref}
          className="actions-summary__content pdf-all-categories"
          style={{ display: showPdfContent ? "block" : "none" }}
        >
          {showPdfContent && (
            <div className="actions-summary__header">
              <h2>{translations.actionTab || "Actions"} ({totalActions})</h2>
            </div>
          )}

          {[1, 2, 3, 4, 5].map((categoryId) => {
            const categoryActions = actions[categoryId] || [];
            if (categoryActions.length === 0) return null;

            return (
              <div
                key={categoryId}
                className="category-section"
                style={{
                  padding: "20px",
                  boxSizing: "border-box",
                  width: "100%",
                }}
              >
                <div className="category-headline">
                  {icon(categoryId) && (
                    <IconContext.Provider value={{ className: "category-icon", color: getCategoryColorFull(categoryId), size: "24px" }}>
                      {icon(categoryId)}
                    </IconContext.Provider>
                  )}
                  <h3 style={{ color: getCategoryColorFull(categoryId) }}>
                    {getCategoryName(categoryId)}
                  </h3>
                </div>
                {categoryActions.map((action, index) => (
                  <div key={index} className="action-item">
                    <h4>{action.title}</h4>
                    <p>{action.description}</p>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className="actions-summary">
        <div className="actions-summary__header">
          <h2>{translations.actionTab || "Actions"} ({totalActions})</h2>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="expand-collapse-button"
            aria-label={isExpanded ? "Collapse actions" : "Expand actions"}
          >
            <IconContext.Provider value={{ className: "expand-icon" }}>
              {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
            </IconContext.Provider>
          </button>
        </div>

        {isExpanded && (
          <>
            <div className="actions-summary__tabs">
              {[1, 2, 3, 4, 5].map((categoryId) => (
                <button
                  key={categoryId}
                  className={`tab-button ${selectedCategory === categoryId ? "active" : ""}`}
                  onClick={() => setSelectedCategory(categoryId)}
                  style={{
                    color: selectedCategory === categoryId ? getCategoryColorFull(categoryId) : undefined,
                    borderBottom: selectedCategory === categoryId ? `2px solid ${getCategoryColorFull(categoryId)}` : "none",
                  }}
                >
                  {icon(categoryId) && (
                    <IconContext.Provider value={{ className: "tab-icon", color: selectedCategory === categoryId ? getCategoryColorFull(categoryId) : undefined, size: "20px" }}>
                      {icon(categoryId)}
                    </IconContext.Provider>
                  )}
                  {getCategoryName(categoryId)}
                </button>
              ))}
            </div>
            {renderSelectedCategory()}
          </>
        )}

        {renderAllCategoriesForPDF()}
      </div>
    );
  }
);

export default ActionsSummary;